import type { AgentDto, RunSettingsOverride } from '@af/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LiveStatusLabel } from '@/components/dashboard/AgentsLive';
import { ComposerMode } from '@/components/run/ComposerMode';
import { SetupGate } from '@/components/setup/SetupGate';
import { AgentOrb } from '@/components/ui/agent-orb';
import { IconChevron, IconSparkle } from '@/components/ui/icons';
import { Empty } from '@/components/ui/misc';
import { modelName, topManager } from '@/lib/agent-utils';
import { call } from '@/lib/api';
import { uploadDrafts, useAttachmentDraft } from '@/lib/chat-attachments';
import { useChatView } from '@/lib/chat-view';
import { relTime } from '@/lib/format';
import { useAgentsLive } from '@/lib/live-agents';
import { useCan } from '@/lib/permissions';
import { notifyError, useAgents, useConversations } from '@/lib/queries';
import { liveRunStore } from '@/lib/run-resume';
import { cn } from '@/lib/utils';
import { Composer } from './Composer';

/**
 * The new-chat screen: pick who to talk to (the Company chat or one agent), write the first message
 * (screenshots can be pasted or dropped) and send — the conversation is created on send, so an
 * abandoned "new chat" never leaves an empty conversation behind.
 */
export function NewChat() {
  const { t } = useTranslation();
  const search = useRouterState({ select: (s) => s.location.search }) as { agent?: string };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const agents = useAgents();
  const live = useAgentsLive();
  const conversations = useConversations();
  const canCreate = useCan('conversation:create');
  const canRun = useCan('agent:run');
  const [view] = useChatView();
  const draft = useAttachmentDraft();
  const [text, setText] = useState('');
  const [runSettings, setRunSettings] = useState<RunSettingsOverride>({});
  const [sending, setSending] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const usable = useMemo(
    () => (agents.data ?? []).filter((a) => a.status === 'active' || a.status === 'paused'),
    [agents.data],
  );
  const [picked, setPicked] = useState<string | null>(search.agent ?? null);
  useEffect(() => {
    if (search.agent) setPicked(search.agent);
  }, [search.agent]);
  const agent: AgentDto | undefined =
    usable.find((a) => a.id === picked) ?? topManager(agents.data) ?? usable[0];
  const statusOf = useMemo(() => new Map(live.list.map((l) => [l.agent.id, l.status])), [live.list]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: focus the box when the agent changes
  useEffect(() => {
    inputRef.current?.focus({ preventScroll: true });
  }, [agent?.id]);

  const recent = (conversations.data ?? []).filter((c) => c.agentId === agent?.id).slice(0, 4);
  const shown = showAll ? usable : usable.slice(0, 6);

  const start = async () => {
    const value = text.trim();
    if (!agent || sending || (!value && !draft.items.length)) return;
    setSending(true);
    try {
      const c = await call('createConversation', { body: { agentId: agent.id } });
      const attachmentIds = draft.items.length ? await uploadDrafts(c.id, draft.items) : [];
      const r = await call('sendMessage', {
        params: { id: c.id },
        body: { runSettings, text: value, ...(attachmentIds.length ? { attachmentIds } : {}) },
      });
      liveRunStore.set(c.id, r.runId);
      setText('');
      draft.clear();
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      void navigate({ to: '/chat/$conversationId', params: { conversationId: c.id } });
    } catch (err) {
      notifyError(err);
    } finally {
      setSending(false);
    }
  };

  if (agents.isPending) return null;
  if (!usable.length)
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto rounded-2xl border border-border bg-bg">
        {/* A new user lands here first: solve provider → model → agent in place, then chat. */}
        <SetupGate need="agent">
          <div className="flex flex-1 items-center justify-center">
            <Empty icon=">">
              {t('chats.noAgents')}{' '}
              <Link to="/agents/new" className="text-accent hover:underline">
                {t('chats.createAgent')}
              </Link>
            </Empty>
          </div>
        </SetupGate>
      </div>
    );

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto rounded-2xl border border-border bg-bg">
      <div className="mx-auto flex w-full max-w-[760px] flex-col gap-6 px-4 py-4 sm:px-6 sm:py-8 lg:py-12">
        <div>
          <h1 className="text-[22px] font-medium tracking-tight sm:text-[28px]">{t('chats.startTitle')}</h1>
          <p className="mt-1 text-sm text-muted">{t('chats.startHint')}</p>
        </div>

        <section aria-label={t('chats.pickAgent')} className="flex flex-col gap-2">
          <div className="-mx-4 flex snap-x gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0 sm:pb-0 lg:grid-cols-3">
            <Link
              to="/master"
              className="flex w-[220px] shrink-0 snap-start items-center gap-3 rounded-xl border border-border sm:w-auto sm:shrink bg-[linear-gradient(135deg,color-mix(in_srgb,var(--accent)_10%,transparent),color-mix(in_srgb,var(--violet)_10%,transparent))] p-3 transition-colors hover:border-accent/50"
            >
              <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-[linear-gradient(135deg,var(--accent),var(--violet))] text-white">
                <IconSparkle size={17} />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium">{t('chats.company')}</span>
                <span className="block truncate text-xs text-muted">{t('chats.companyShort')}</span>
              </span>
            </Link>
            {shown.map((a) => {
              const selected = a.id === agent?.id;
              const status = statusOf.get(a.id);
              return (
                <button
                  key={a.id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setPicked(a.id)}
                  className={cn(
                    'flex w-[220px] shrink-0 snap-start items-center gap-3 rounded-xl border p-3 text-left transition-colors sm:w-auto sm:shrink',
                    selected
                      ? 'border-accent bg-accent/8 ring-2 ring-accent/20'
                      : 'border-border hover:bg-hover',
                  )}
                >
                  <AgentOrb seed={a.id} size={36} avatar={a.avatar} name={a.name} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{a.name}</span>
                    <span className="block truncate text-xs text-muted">{a.roleTitle || modelName(a)}</span>
                    {status && status !== 'idle' ? (
                      <span className="mt-0.5 block">
                        <LiveStatusLabel status={status} />
                      </span>
                    ) : null}
                  </span>
                </button>
              );
            })}
          </div>
          {usable.length > 6 ? (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="self-start py-2 text-xs text-accent hover:underline"
            >
              {showAll ? t('chats.showFewer') : t('chats.showAll', { count: usable.length })}
            </button>
          ) : null}
        </section>

        {canCreate && canRun && agent ? (
          <Composer
            controls={<ComposerMode value={runSettings} onChange={setRunSettings} disabled={sending} />}
            bare
            view={view === 'advanced' ? 'standard' : view}
            value={text}
            onChange={setText}
            onSubmit={() => void start()}
            inputRef={inputRef}
            ariaLabel={t('chats.messageTo', { name: agent.name })}
            placeholder={t('chats.placeholder', { name: agent.name })}
            disabled={sending || agent.status !== 'active'}
            attachments={draft}
            footer={
              <span className="flex min-w-0 items-center gap-1.5 truncate">
                <AgentOrb seed={agent.id} size={14} avatar={agent.avatar} />
                <span className="truncate">
                  {agent.status === 'active'
                    ? `${agent.name} · ${modelName(agent)}`
                    : t('chats.paused', { name: agent.name })}
                </span>
              </span>
            }
          />
        ) : null}

        {recent.length ? (
          <section aria-label={t('chats.recentWith', { name: agent?.name ?? '' })}>
            <h2 className="mb-1.5 text-xs font-medium text-muted">
              {t('chats.recentWith', { name: agent?.name ?? '' })}
            </h2>
            <ul className="overflow-hidden rounded-xl border border-border">
              {recent.map((c) => (
                <li key={c.id} className="border-b border-border last:border-b-0">
                  <Link
                    to="/chat/$conversationId"
                    params={{ conversationId: c.id }}
                    className="flex items-center gap-3 px-3 py-2.5 hover:bg-hover"
                  >
                    <span className="min-w-0 flex-1 truncate text-sm">{c.title}</span>
                    <span className="shrink-0 text-xs text-faint">{relTime(c.updatedAt)}</span>
                    <IconChevron size={12} className="shrink-0 text-faint" />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    </div>
  );
}
